/**
 * What the NEXT set's stepper is pre-filled with (`D110`, `T48`) — keep,
 * increase or reduce, decided from the last logged set and the item's own
 * targets.
 *
 * ── SESSION-LOCAL. THE SERVER'S `advance-plan` IS THE AUTHORITY BETWEEN
 *    SESSIONS (`T33`). ────────────────────────────────────────────────────
 * Nothing here computes progression from one session to the next — that is
 * the engine's `progression.ts`, run on the server when a session ends, with
 * ceilings this file does not have. What lives here is only the within-bout
 * courtesy of a sensible default: the person logged a set ten seconds ago,
 * and the row for the next one should already say what they will most likely
 * do. Every number is editable; a suggestion is not a prescription.
 *
 * The two thresholds restate `progression.ts`'s named constants — a set that
 * felt comfortable (RPE ≤ 7) at the top of the range earns more load, a set
 * at near-failure (RPE ≥ 9) sheds one step — because the app cannot import
 * the module (`D118`) and the in-session rule must agree with the
 * between-session one about what "comfortable" and "near failure" mean.
 */
import { HIGH_RPE_THRESHOLD } from "./rpe-anomaly";
import { DEFAULT_LOAD_STEP_KG } from "./set-entry";
/** RPE at most this counts as comfortable — `PROGRESSION_MAX_COMFORT_RPE`. */
export const MAX_COMFORT_RPE = 7;
export function suggestNextSet(params) {
    const { last, targets } = params;
    const stepKg = params.stepKg ?? DEFAULT_LOAD_STEP_KG;
    // First set: the prescription itself — the working load, aiming at the top
    // of the range.
    if (last === null) {
        return { kind: "keep", load: targets.load, reps: targets.repsMax };
    }
    const load = last.load ?? targets.load;
    const reps = last.reps ?? targets.repsMax;
    // Near failure: shed a step and settle for the bottom of the range. For a
    // bodyweight item there is no load to shed, so the reps carry the cut.
    if (last.rpe !== null && last.rpe >= HIGH_RPE_THRESHOLD) {
        return {
            kind: "reduce",
            load: load === null ? null : Math.max(0, roundLoad(load - stepKg)),
            reps: targets.repsMin,
        };
    }
    // Comfortable at the top of the range: double progression in miniature —
    // one step up, back to the bottom of the range. Bodyweight progresses by a
    // rep instead, because a rep is the only knob it has.
    if (last.reps !== null &&
        last.reps >= targets.repsMax &&
        last.rpe !== null &&
        last.rpe <= MAX_COMFORT_RPE) {
        if (load === null) {
            return { kind: "increase", load: null, reps: last.reps + 1 };
        }
        return {
            kind: "increase",
            load: roundLoad(load + stepKg),
            reps: targets.repsMin,
        };
    }
    // Everything else: same again, reps clamped into the prescribed range so a
    // short set does not quietly lower the target for the rest of the bout.
    return {
        kind: "keep",
        load,
        reps: Math.min(Math.max(reps, targets.repsMin), targets.repsMax),
    };
}
/** Kills the float drift of repeated 2.5 arithmetic. */
function roundLoad(value) {
    return Math.round(value * 100) / 100;
}
//# sourceMappingURL=next-suggestion.js.map